import { ArrowRight, Archive, FileText, Loader2 } from "lucide-react";
import { fmtMoney } from "@/lib/assessment";
import PromoCodeInput from "./PromoCodeInput";

/* eslint-disable react/no-unescaped-entities */
const ARCHIVE_FEATURES = [
  "Keep your assessment & AI Career Report on file for 12 months",
  "Download a PDF copy of your résumé any time",
  "Pause active job-matching without losing your data",
  "Return later and pick up exactly where you left off",
  "Paid once a year — no monthly billing to manage",
];

export default function ArchiveCard({
  archive,
  membershipActive,
  busy,
  onCheckout,
  promo,
  onPromoApply,
  onPromoClear,
}) {
  const pricing = archive.pricing;
  const active = archive.status === "active";
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  return (
    <section
      className="rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col"
      data-testid="subscribe-archive-card"
    >
      <header className="flex items-center gap-3 mb-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
          <Archive className="h-5 w-5" />
        </span>
        <div>
          <h2 className="text-lg font-medium text-slate-900">My Archive</h2>
          <p className="text-xs text-slate-500">
            Keep your data on file · 12 months prepaid
          </p>
        </div>
      </header>

      <div className="flex items-baseline gap-2 mt-2">
        <span
          className="text-5xl font-medium text-slate-900"
          data-testid="subscribe-archive-monthly"
        >
          {fmtMoney(pricing.monthly_base_aud, "AUD")}
        </span>
        <span className="text-slate-500">/ month + GST</span>
      </div>
      <p
        className="text-xs text-slate-500 mt-1.5"
        data-testid="subscribe-archive-annual"
      >
        Charged once a year · {fmtMoney(pricing.base_aud, "AUD")} + 10% GST{" "}
        {fmtMoney(pricing.gst_aud, "AUD")} ={" "}
        <span className="font-medium text-slate-700">
          {fmtMoney(pricing.total_aud, "AUD")} incl. GST
        </span>{" "}
        / year
      </p>

      <p className="mt-3 text-sm text-slate-600">
        For when the active programme has run its course but you'd rather not
        delete everything. Your assessment, AI report and résumé stay
        accessible — return any time without starting over.
      </p>

      <ul
        className="mt-6 space-y-2.5 flex-1"
        data-testid="subscribe-archive-features"
      >
        {ARCHIVE_FEATURES.map((f) => (
          <li
            key={f}
            className="flex items-start gap-2.5 text-sm text-slate-700"
          >
            <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-amber-50 text-amber-700 border border-amber-100">
              <FileText className="h-3 w-3" />
            </span>
            {f}
          </li>
        ))}
      </ul>

      <div className="mt-6 rounded-xl bg-slate-50 border border-slate-100 p-4 text-sm text-slate-600">
        <p className="font-medium text-slate-800 mb-1">
          Eligibility &amp; renewal
        </p>
        <p>
          Available after your first 3 months of membership. Renews annually;
          cancel any time and access continues until your current 12 months are
          up.
        </p>
      </div>

      <button
        type="button"
        onClick={() => onCheckout("archive")}
        disabled={busy?.plan === "archive"}
        className="mt-6 inline-flex items-center gap-2 rounded-xl bg-amber-600 px-5 py-3 text-sm font-medium text-white shadow-md hover:bg-amber-700 disabled:opacity-60"
        data-testid="subscribe-archive-pay-button"
      >
        {busy?.plan === "archive" ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Archive className="h-4 w-4" />
        )}
        {promo && promo.requires_payment === false
          ? `Activate free — ${promo.duration_months} ${
              promo.duration_months === 1 ? "month" : "months"
            }`
          : active
            ? `Renew Archive (${fmtMoney(
                promo?.total_aud ?? pricing.total_aud,
                "AUD",
              )} incl. GST)`
            : `Start Archive — ${fmtMoney(
                promo?.total_aud ?? pricing.total_aud,
                "AUD",
              )} incl. GST / year`}
        <ArrowRight className="h-4 w-4" />
      </button>
      <PromoCodeInput
        plan="archive"
        applied={promo}
        onApply={onPromoApply}
        onClear={onPromoClear}
      />
      {(active || membershipActive) && (
        <div className="mt-3 space-y-2">
          <a
            href={`${backendUrl}/api/archive/resume.pdf`}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-5 py-2.5 text-sm font-medium text-amber-800 hover:bg-amber-100"
            data-testid="subscribe-resume-download"
          >
            <FileText className="h-4 w-4" /> Download my résumé (PDF)
          </a>
          <a
            href={`${backendUrl}/api/archive/cover-letter.pdf`}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            data-testid="subscribe-cover-letter-download"
          >
            <FileText className="h-4 w-4" /> Download AI cover letter (PDF)
          </a>
        </div>
      )}
      <p className="mt-2 text-xs text-slate-500">
        Secured by Stripe. AUD billing.
      </p>
    </section>
  );
}
